import React from "react";

import { cn } from "@workspace/web-ui/lib/utils";
import { PatientSessions, Session } from "../../api/types";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { formatToMonthDayYear } from "@workspace/lib/date";
import { useActiveSessionStore } from "../../store/active-session-store";
import { CheckCircle, Circle, UserRoundIcon } from "lucide-react";


interface SessionProps {
    sessionData?: PatientSessions;
}

export const Sessions = ({ sessionData }: SessionProps) => {
    const { open } = useActiveSessionStore()
    const defaultSessions = {
        status: '',
        message: '',
        sessions: []
    };
    console.log("sessionData in sessions", sessionData)

    return (
        <SessionCard patient={sessionData ?? defaultSessions} open={open} />
    )
}

interface TimelineDetailsProps {
    title: string;
    encounter_datetime: string;
    alfie_session: string;
    payment_status: string;
    treatment_completed: boolean;
    session: Session;
}

const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: "", time: "" };

    try {
        // Parse the ISO datetime string
        const dateObj = new Date(dateTimeString);

        // Format date as YYYY-MM-DD
        const date = dateObj.toISOString().split('T')[0];

        // Format time as HH:MM
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const time = `${hours}:${minutes}`;

        return { date, time };
    } catch (error) {
        console.error("Error parsing datetime:", error);
        return { date: "", time: "" };
    }
};

const SessionDetails = ({ title, encounter_datetime, payment_status, treatment_completed, session }: TimelineDetailsProps) => {
    const { date, time } = formatDateTime(encounter_datetime);

    return (
        <div className="flex flex-col gap-2 w-full">
            <h1 className="text-[18px] text-[#1A202C] font-medium">{title}</h1>
            {date && time && (<div className="flex items-center">
                <span className="text-xs font-normal">Started: {formatToMonthDayYear(date)}</span>
            </div>)}
            <div className="flex gap-2">
                {payment_status === "paid" && (
                    <div className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                        "bg-[#E6FAF2] text-[#00C781]"
                    )}>
                        <CheckCircle className="w-3 h-3" />
                        Payment Complete
                    </div>
                )}
                <div className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                    treatment_completed
                        ? "bg-[#6E23C91A] text-[#6E23C9]"
                        : session?.state === "in_progress"
                            ? "bg-[#4272DD1A] text-[#4272DD]" // Orange/yellow styling for pending review
                            : "bg-[#FFF4E6] text-[#F59E0B]"
                )}>
                    {treatment_completed ? (
                        <CheckCircle className="w-3 h-3" />
                    ) : session?.state === "in_progress" ? (
                        <Circle className="w-3 h-3" />
                    ) : (
                        <Circle className="w-3 h-3" />
                    )}
                    {treatment_completed
                        ? "Treatment completed"
                        : session?.state === "in_progress"
                            ? "In Progress"
                            : "Pending Review"
                    }
                </div>
            </div>
        </div>
    )
}

const SessionCard = ({ patient, open }: { patient: PatientSessions, open?: (session?: PatientSessions['sessions'][0]) => void }) => {
    console.log("patient in session card", patient.sessions)

    const filteredSessions = patient?.sessions?.filter(session => (session?.diagnosis?.length ?? 0) > 0) || [];

    return (
        <div className={cn("rounded-[8px] flex flex-col gap-4 items-start w-full shadow-sm cursor-pointer",)}>
            {filteredSessions.length ? (
                filteredSessions
                    .map((session) => (
                        <React.Fragment key={`${session.session_id}-${session.start_datetime}`}>
                            <div className="flex flex-col md:flex-row justify-between w-full gap-4 border border-[#DCDCDE] bg-white p-4 rounded-[8px]">
                                <div className={cn("flex gap-5 items-start w-full")}>
                                    <SessionDetails
                                        title={`${session?.diagnosis?.[0]?.disease_name}`}
                                        encounter_datetime={session.start_datetime}
                                        alfie_session={session.alfie_session}
                                        payment_status={session.payment_status}
                                        treatment_completed={session.treatment_completed}
                                        session={session}
                                    />
                                </div>
                                <div className="flex w-full md:w-[20%]">
                                    <Button
                                        className={cn(buttonVariants({ variant: "outline" }), "bg-[#4272DD] w-full rounded-[8px] border-2 border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                        onClick={() => open?.(session)}
                                    >
                                        See Details
                                    </Button>
                                </div>
                            </div>
                        </React.Fragment>
                    ))
            ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-[#DCDCDE] rounded-[8px] w-full">
                    <UserRoundIcon
                        className="size-16 text-[#FFAB1F]"
                    />
                    <h3 className="text-[18px] font-medium text-[#1A202C] mb-1 mt-2">No sessions found</h3>
                    <p className="text-sm text-[#718096] text-center max-w-sm">
                        There are no sessions with diagnosis records available for this patient.
                    </p>
                </div>
            )}
        </div>
    )
}



